/**
 * This component contains code to edit a comment,
 * This modal is only editable for first 24 hours, once a comment is added
 */
import React from "react";
import {
  Button,
  Typography,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  IconButton,
  DialogActions
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import { ReactComponent as EditCommentIcon } from "assets/icons/editComment.svg";
import { EditCommentFields } from "./EditComment.types";
import DashedDivider from "common/ui/DashedDivider";
import useForm from "hooks/useForm";
import dayjs from "dayjs";
import * as constants from "helpers/constants";
import { useTranslation } from "react-i18next";
import { useUpdateRunComment } from "services/runs.service";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "services/users.service";
import SaveIcon from "@mui/icons-material/Save";
import Divider from "@mui/material/Divider";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { selectEditComment } from "context/modal.selector";
import { setEditComment } from "context/modal.context";
import toast from "react-hot-toast";
import LoadingButton from "common/ui/LoadingButton";
import { decode, encode } from "utils/common";

function EditComment() {
  const { t } = useTranslation();
  const user = useUser();
  const dispatch = useAppDispatch();
  const props = useAppSelector(selectEditComment);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    clearErrors
  } = useForm<EditCommentFields>({
    defaultValues: {
      comment: ""
    }
  });

  React.useEffect(() => {
    if (props.open) {
      reset({
        comment: decode(props.comment?.comments ?? "")
      });
    }
  }, [props.open]);

  const queryClient = useQueryClient();

  const updateComment = useUpdateRunComment(onRunCommentUpdated);

  const canUpdateComment =
    dayjs().isBefore(dayjs(props.comment?.dateAdded).add(24, "hours")) &&
    (user.data?.email?.toLowerCase() ===
      props.comment?.commentUpdatedBy?.toLowerCase() ||
      props.comment?.commentUpdatedBy === null);

  function onRunCommentUpdated(_data, error) {
    queryClient.invalidateQueries(["run-comments"]);
    let message = t("editComment.successMessage");

    if (!error) {
      toast.success(message);
    }

    clearErrors();
    onModalDismiss();
  }

  function onModalDismiss() {
    clearErrors();
    dismiss();
  }

  function dismiss() {
    dispatch(
      setEditComment({
        open: false,
        comment: null
      })
    );
  }
  function onSubmit(data: EditCommentFields) {
    updateComment.mutate({
      id: props.comment?.id,
      runId: props.comment?.runId,
      comment: encode(data.comment)
    });
  }

  return (
    <>
      <Dialog open={props.open} scroll="body" className="highlight">
        <DialogTitle sx={{ paddingBottom: 0 }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <EditCommentIcon />
              <Typography
                color="primary.main"
                variant="h6"
                fontWeight="medium"
                alignSelf="center"
              >
                {t("editComment.comment")}
              </Typography>
            </Stack>
            <Stack direction="row">
              <Typography
                color="gray.main"
                variant="subtitle1"
                alignSelf="center"
                sx={{ marginLeft: 5 }}
              >
                {t("editComment.dateTime")}
              </Typography>
              <Typography
                color="black"
                variant="subtitle1"
                alignSelf="center"
                sx={{ marginLeft: 2 }}
              >
                {dayjs(props.comment?.runCommentTime).format(
                  constants.DATE_TIME_FORMAT
                )}
              </Typography>
              <IconButton onClick={onModalDismiss}>
                <Close />
              </IconButton>
            </Stack>
          </Stack>
          <DashedDivider />
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack direction="row" marginY={1}>
              <Typography variant="body2" color="gray.main" alignSelf="center">
                {props.comment?.commentUpdatedBy}
              </Typography>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: "gray.main", marginX: 1, marginY: 0.5 }}
              />
              <Typography variant="body2" alignSelf="center" color="gray.main">
                {dayjs(props.comment?.dateUpdated).format(
                  constants.DATE_TIME_FORMAT
                )}
              </Typography>
            </Stack>
            <TextField
              {...register("comment", {
                required: t("editComment.required"),
                pattern: {
                  value: /^$|\S/,
                  message: t("editComment.required")
                }
              })}
              helperText={errors.comment?.message}
              error={!!errors.comment}
              variant="outlined"
              rows={5}
              multiline={true}
              fullWidth
              disabled={!canUpdateComment}
              sx={{ marginBottom: 2 }}
            />

            <DashedDivider />
            <DialogActions
              sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
            >
              {canUpdateComment ? (
                <LoadingButton
                  loading={updateComment.isLoading}
                  variant="outlined"
                  color="primary"
                  type="submit"
                  startIcon={<SaveIcon />}
                >
                  {t("common.save")}
                </LoadingButton>
              ) : (
                <Button
                  onClick={onModalDismiss}
                  variant="outlined"
                  size="large"
                  startIcon={<Close />}
                  color="gray"
                >
                  {t("common.close")}
                </Button>
              )}
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EditComment;
